import { useEffect, useState } from 'react';

/**
 * Viewport size, as a boolean per breakpoint.
 *
 * The landing page is built from inline styles, and inline styles cannot
 * carry media queries — which is exactly why the nav had no mobile layout and
 * ran off the side of the screen. Rather than rewrite every section into CSS
 * classes, the breakpoint is read in JavaScript and the style object chosen
 * from it.
 *
 * `matchMedia` rather than a resize listener on `innerWidth`: the browser
 * only fires it when a query's answer actually changes, so this does not
 * re-render on every pixel of a drag.
 *
 * Breakpoints match MUI's, so the landing page and the dashboards agree on
 * what "mobile" means.
 */

const QUERIES = {
  isMobile: '(max-width: 599px)',
  isTablet: '(min-width: 600px) and (max-width: 899px)',
  isDesktop: '(min-width: 900px)',
  // The nav collapses earlier than the tablet boundary: four links, a logo, a
  // theme toggle and a CTA stop fitting well before 600px.
  isCompactNav: '(max-width: 860px)',
  // Smallest phones still in use (iPhone SE 1st gen is 320px). At this width
  // a wordmark, a theme toggle, a worded CTA and a menu button do not fit on
  // one row, so the toggle moves into the menu and the CTA label shortens.
  isNarrow: '(max-width: 380px)',
};

const read = () => {
  // Guard for SSR and for any test environment without matchMedia.
  if (typeof window === 'undefined' || !window.matchMedia) {
    return { isMobile: false, isTablet: false, isDesktop: true, isCompactNav: false, isNarrow: false };
  }
  return Object.fromEntries(
    Object.entries(QUERIES).map(([k, q]) => [k, window.matchMedia(q).matches]),
  );
};

export default function useBreakpoint() {
  const [state, setState] = useState(read);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;

    const lists = Object.values(QUERIES).map((q) => window.matchMedia(q));
    const update = () => setState(read());

    lists.forEach((l) => {
      // Safari below 14 only has the deprecated addListener.
      if (l.addEventListener) l.addEventListener('change', update);
      else l.addListener(update);
    });

    // The first paint used whatever the initial read returned; re-read once
    // in case the viewport settled differently (rotation, zoom restore).
    update();

    return () => lists.forEach((l) => {
      if (l.removeEventListener) l.removeEventListener('change', update);
      else l.removeListener(update);
    });
  }, []);

  return state;
}
